/**
 * Canonical window-size → human-label maps, shared across the app.
 * Keyed by the trading-day window size (window * step): 10 / 20 / 65 / 130 / 260.
 *
 * Two label styles are in use and deliberately kept distinct:
 *   - WINDOW_LABELS       title-case, hyphenated ("3-Month") — Track Record surfaces
 *   - WINDOW_LABELS_LONG  spaced, lower-case ("3 months")    — the matcher dropdown
 */

/** Title-case, hyphenated. String-keyed (matches JSON payload window keys). */
export const WINDOW_LABELS: Record<string, string> = {
  "10": "2-Week",
  "20": "4-Week",
  "65": "3-Month",
  "130": "6-Month",
  "260": "1-Year",
};

/** Spaced, lower-case. Number-keyed (matcher window values are numbers). */
export const WINDOW_LABELS_LONG: Record<number, string> = {
  10: "2 weeks",
  20: "4 weeks",
  65: "3 months",
  130: "6 months",
  260: "1 year",
};

// /api/query hybrid-window matching is dimensionally valid only for these three
// display horizons: each is represented by the same 65-point retrieval vector
// (their raw sampling step differs). Keep this list shared by URL parsing, the
// control state, and request serialization so the client cannot drift internally.
export const HYBRID_MATCH_WINDOWS = [65, 130, 260] as const;
export const ALL_HYBRID_WINDOWS_PARAM = HYBRID_MATCH_WINDOWS.join(",");

export function isHybridMatchWindow(window: number): boolean {
  return (HYBRID_MATCH_WINDOWS as readonly number[]).includes(window);
}

export function isAllHybridWindowsParam(value: string | null): boolean {
  if (!value) return false;
  const parsed = value.split(",").map((part) => Number.parseInt(part.trim(), 10));
  const uniqueSorted = [...new Set(parsed)].sort((a, b) => a - b);
  return uniqueSorted.length === HYBRID_MATCH_WINDOWS.length
    && uniqueSorted.every((window, index) => window === HYBRID_MATCH_WINDOWS[index]);
}

export function hybridTargetWindowsParam(sourceWindow: number, enabled: boolean): string | undefined {
  return enabled && isHybridMatchWindow(sourceWindow) ? ALL_HYBRID_WINDOWS_PARAM : undefined;
}

/** Canonical shortest→longest ordering of the window keys. */
export const WINDOW_ORDER = ["10", "20", "65", "130", "260"];

/**
 * Production strict-pass floor per window, 0-100 scale (matches the Matcher's `score`
 * control) — mirrors SLICE_PARAMS' min_score in daily.py (stocks_api). A featured signal
 * already cleared this bar for its window, so deep-linking the Matcher at this score
 * reproduces the exact analog set the nightly pipeline counted, rather than the Matcher's
 * own MATCHER_DEFAULT_SCORE (QueryMatcher.tsx), which is deliberately much lower.
 *
 * Prefer the live value carried on the /api/featured response (`FeaturedStockData.min_score`,
 * a 0-1 fraction) over this constant when it's available — it can never drift from what
 * daily.py actually used. This is a fallback for callers without that response.
 */
export const PRODUCTION_MIN_SCORE: Record<number, number> = {
  10: 99,
  20: 93,
  65: 80,
  130: 80,
  260: 80,
};

/** Production committed forecast horizon (trading days) per window — mirrors SLICE_PARAMS'
 * forecast_window in daily.py (stocks_api). This is the metric the track record accrues
 * against; never derive it from anything but this mirror. */
export const PRODUCTION_FORECAST_WINDOW: Record<number, number> = {
  10: 1,
  20: 3,
  65: 5,
  130: 10,
  260: 20,
};
